import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { Terminal as XTerm } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';
import { useEffect, useRef, useState } from 'react';

const THEME = {
  background: 'rgba(0,0,0,0)', // átlátszó: a keret (a-termwrap / a-dash) adja a hátteret
  foreground: '#e6e6ea',
  cursor: '#ff5c1a',
  cursorAccent: '#000000',
  selectionBackground: '#264f78',
  black: '#000000',
  brightBlack: '#52525b',
};
const FONT = "'IBM Plex Mono', ui-monospace, 'JetBrains Mono', Menlo, monospace";

interface Props {
  name?: string;
  projectId?: number | null;
  // Meglévő session újracsatlakoztatása (fülhöz), ha ismert.
  sessionId?: string;
  // A szerver által adott session-id visszajelzése a szülőnek (fül-kezelés).
  onSession?: (sessionId: string) => void;
  // Csak meglévő sessionhöz csatlakozik (munkás-ablak): ha már nincs, nem indít újat.
  attachOnly?: boolean;
  onGone?: () => void;
  // A kapcsolódáskor kapjon-e fókuszt (a munkás-blokkok ne vegyék el a fő ablaktól).
  autoFocus?: boolean;
}

// A terminál betűtípusát a megnyitás ELŐTT betöltjük: az xterm a cellaméretet egyszer méri, és ha közben cserélődik
// a betű, a szöveg elcsúszik. Legfeljebb 2 mp-et várunk rá, utána a tartalék betűvel indulunk.
function loadTermFont(): Promise<unknown> {
  if (!document.fonts?.load) return Promise.resolve();
  const want = Promise.all([document.fonts.load('13px "IBM Plex Mono"'), document.fonts.load('bold 13px "IBM Plex Mono"')]);
  return Promise.race([want, new Promise((r) => setTimeout(r, 2000))]).catch(() => undefined);
}

export function TerminalView({ name, projectId, sessionId, onSession, attachOnly, onGone, autoFocus = true }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onSessionRef = useRef(onSession); onSessionRef.current = onSession;
  const onGoneRef = useRef(onGone); onGoneRef.current = onGone;
  const [state, setState] = useState<'wait' | 'connecting' | 'live' | 'lost' | 'ended'>('wait');

  // Egyszer kapcsolódunk fülönként; a fül React-key-e adja az élettartamot.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let disposed = false;
    let term: XTerm | null = null;
    let fit: FitAddon | null = null;
    let ws: WebSocket | null = null;
    let closedByUs = false;
    let reconnectTimer: number | undefined;
    let ro: ResizeObserver | null = null;
    let retries = 0;
    let currentSession: string | null = sessionId ?? null;
    // Alulra tapadás: ha az alján vagy, ott is maradsz; ha felgörgettél olvasni, nem rángat le.
    let stick = true; let forceBottomUntil = 0;
    // DOM-renderelő (nincs WebGL): több terminál mellett a böngésző eldobná a régebbi WebGL-környezeteket, és üres maradna a kép.

    const hasRoom = () => container.clientWidth >= 80 && container.clientHeight >= 50;
    const sane = () => Boolean(term && term.cols >= 20 && term.rows >= 5);
    const doFit = () => { if (!term || !fit || !hasRoom()) return false; try { fit.fit(); } catch { return false; } return sane(); };
    // Apró (rejtett/félkész elrendezésből jövő) méretet sosem küldünk: attól csúszik a CLI kimenete a bal felső sarokba.
    const sendResize = () => { if (term && sane() && ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows })); };

    const connect = () => {
      if (disposed || !term) return;
      if (!doFit()) { reconnectTimer = window.setTimeout(connect, 400); return; } // még nincs értelmes méret (rejtett fül): várunk
      setState('connecting');
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      const params = new URLSearchParams();
      if (currentSession) params.set('session', currentSession);
      if (name) params.set('name', name);
      if (projectId != null) params.set('project', String(projectId));
      if (attachOnly) params.set('attach', '1');
      params.set('cols', String(term.cols)); params.set('rows', String(term.rows));
      const sock = new WebSocket(`${proto}://${location.host}/ws/terminal?${params.toString()}`);
      ws = sock;

      sock.onmessage = (ev: MessageEvent<string>) => {
        if (!term || sock !== ws) return;
        let msg: { type: string; data?: string; session?: string; code?: number };
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === 'output' && msg.data !== undefined) {
          // Csatlakozás után (a pillanatkép és az első kimenet) mindig az aljára görgetünk; utána csak ha ott voltál.
          const t = term; const force = Date.now() < forceBottomUntil;
          t.write(msg.data, () => { if (force || stick) t.scrollToBottom(); });
        } else if (msg.type === 'ready') {
          // Minden (újra)csatlakozás tiszta lappal indul: a szerver a képernyő pontos pillanatképét küldi.
          term.reset();
          forceBottomUntil = Date.now() + 2500; stick = true;
          retries = 0; setState('live');
          if (msg.session) { currentSession = msg.session; onSessionRef.current?.(msg.session); }
          sendResize();
          if (autoFocus) term.focus();
        } else if (msg.type === 'gone') {
          term.write('\r\n\x1b[90m[ez az ablak már lezárult]\x1b[0m\r\n');
          closedByUs = true; setState('ended'); onGoneRef.current?.();
        } else if (msg.type === 'exit') {
          term.write(`\r\n\x1b[90m[session vége — kód ${msg.code}]\x1b[0m\r\n`);
          closedByUs = true; setState('ended'); // kilépett pty → ne csatlakozzunk automatikusan újra
          onGoneRef.current?.();
        }
      };
      sock.onclose = () => {
        if (sock !== ws || closedByUs || disposed) return;
        setState('lost'); retries++;
        reconnectTimer = window.setTimeout(connect, Math.min(1000 * retries, 8000));
      };
    };

    void loadTermFont().then(() => {
      if (disposed) return;
      term = new XTerm({ cursorBlink: true, fontFamily: FONT, fontSize: 13, lineHeight: 1.2, theme: THEME, allowProposedApi: true, allowTransparency: true, scrollback: 5000 });
      fit = new FitAddon();
      term.loadAddon(fit);
      term.loadAddon(new WebLinksAddon());
      term.open(container);
      term.onData((d) => { stick = true; if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'input', data: d })); });
      term.onScroll(() => { const b = term!.buffer.active; stick = b.viewportY >= b.baseY - 1; });

      // Méretváltozás / láthatóvá válás: illesztés, a CLI értesítése, és a kép frissítése (rejtett fülről visszatérve).
      let raf = 0;
      ro = new ResizeObserver(() => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => { if (!term || disposed) return; if (doFit()) { sendResize(); if (stick) term.scrollToBottom(); term.refresh(0, term.rows - 1); } });
      });
      ro.observe(container);
      connect();
    });

    // Visszatérés a lapra (másik ablakból, telefon-zárolásból): ha közben megszakadt, azonnal újracsatlakozunk.
    const onVisible = () => { if (document.visibilityState === 'visible' && !closedByUs && !disposed && term && (!ws || ws.readyState === WebSocket.CLOSED)) { clearTimeout(reconnectTimer); retries = 0; connect(); } };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      disposed = true; closedByUs = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ro?.disconnect();
      ws?.close();
      term?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" />
      {(state === 'lost' || state === 'connecting') && <div className="pointer-events-none absolute right-2 top-2 rounded bg-black/80 px-2 py-1 text-[11px] text-amber-300">{state === 'lost' ? 'kapcsolat megszakadt — újracsatlakozás…' : 'csatlakozás…'}</div>}
    </div>
  );
}
